import Text from 'components/shared/Text'
import Box from 'components/shared/Box'
import { FormattedMessage, useIntl } from 'react-intl'
import Radio from 'components/shared/Radio/index'
import Section from 'components/shared/Section'
import { useResolutions } from 'store/resolutions'
import { CARD_DELIVERY_PRICE } from 'constants/prices'
import { formatPrice } from 'helpers/helpers'
import { handleError } from 'helpers/errors'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import { setDeliveryMethod, setShippingPrice } from 'store/unlimited-slice'
import {
    estimatedDeliveryDate,
    isCurrentDateTimeWithinRange,
} from 'helpers/dates'
import DeliveryByPostForm from 'routes/OrderAndPayment/sections/VoucherDetails/DeliveryByPostForm'
import {
    useGetShippingMethodsQuery,
    useCreateCartItemMutation,
    useGetProductQuery,
    useDeleteCartItemByIdMutation,
} from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import { useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useCart } from 'store/cart-slice/hooks'
import Icon from 'components/shared/Icon'

const CardDeliveryOptions = () => {
    const { formatMessage } = useIntl()
    const { appMode } = useApp()
    const { isTablet } = useResolutions()
    const dispatch = useAppDispatch()
    const { deliveryMethod, shippingPrice } = useAppSelector(
        (state) => state.unlimited
    )
    const shippingOrderId = useAppSelector(
        (state) => state.cart.items.shipping?.cartItemId
    )

    const { language } = useApp()
    const { refetchCart } = useCart()
    const { data, isFetching } = useGetShippingMethodsQuery({
        _locale: language,
        codes: ['self_pickup', 'inpost_delivery_address'],
    })
    const { data: shippingData, isFetching: isShippingDataFetching } =
        useGetProductQuery({
            _locale: language,
            productType: 'shipping',
        })
    const [postCartItem, postCartItemStatus] = useCreateCartItemMutation({})
    const [deleteCartItem, deleteStatus] = useDeleteCartItemByIdMutation()
    const isPostShippingMethodProcessing =
        postCartItemStatus.isLoading ||
        deleteStatus.isLoading ||
        isShippingDataFetching

    const shippingId = shippingData?.productId
    const cartCreatorType =
        appMode === 'unlimited-renew' ? 'card-renew' : 'card'

    const handlePostShippingMethod = async () => {
        if (deliveryMethod === 'selfPickup' && shippingId) {
            try {
                dispatch(setDeliveryMethod('shipping'))

                await postCartItem({
                    _locale: language,
                    cartCreatorType,
                    createCartItemCommand: {
                        cartItemId: shippingOrderId || uuidv4(),
                        productId: shippingId,
                        productType: 'shipping',
                        quantity: 1,
                    },
                })
                return refetchCart()
            } catch (error) {
                handleError(error)
            }
        }
        if (shippingOrderId) {
            try {
                dispatch(setDeliveryMethod('selfPickup'))

                await deleteCartItem({
                    cartItemId: shippingOrderId,
                    _locale: language,
                    cartCreatorType,
                })

                refetchCart()
            } catch (error) {
                handleError(error)
            }
        }
    }

    const handleSetDeliveryMethod = (value: string) => {
        if (value === deliveryMethod) return

        handlePostShippingMethod()
    }

    useEffect(() => {
        if (!isFetching) {
            const shippingDetails = data?.shippingMethods?.find(
                (item) => item.deliveryType === 'shipping'
            )

            shippingDetails?.price &&
                dispatch(setShippingPrice(shippingDetails.price))
        }
    }, [isFetching])

    useEffect(() => {
        if (shippingOrderId) dispatch(setDeliveryMethod('shipping'))
    }, [])

    return (
        <Section>
            <Box mb={deliveryMethod === 'shipping' ? [32, 40] : 0}>
                <Text variant="heading2" mb={40}>
                    {formatMessage({
                        id: 'annual.payment.delivery_options_caption',
                        defaultMessage: 'Sposób dostawy kart',
                    })}
                </Text>

                <Radio
                    options={[
                        {
                            option: formatMessage({
                                id: 'annual.payment.delivery_in_park_option',
                                defaultMessage: 'Odbierz w Parku: za darmo!',
                            }),
                            id: 'selfPickup',
                        },
                        {
                            option: `${formatMessage({
                                id: 'annual.payment.delivery_by_courier_option',
                                defaultMessage: 'Wysyłka kurierem: ',
                            })}  ${formatPrice(
                                shippingPrice || CARD_DELIVERY_PRICE,
                                true
                            )}`,
                            id: 'shipping',
                            additionalDescription: isCurrentDateTimeWithinRange(
                                '2024-12-16 00:00',
                                '2024-12-19 15:00'
                            ) ? (
                                <Box direction="row" gap={4}>
                                    <Icon variant="gift" />
                                    <Text
                                        variant="body16"
                                        color="success"
                                        mt={2}
                                    >
                                        <FormattedMessage
                                            id="annual.payment.delivery_by_courier_christmas_description"
                                            defaultMessage="Zamów do 15:00 dn. 19.12 z gwarancją dostawy do świąt!"
                                        />
                                    </Text>
                                </Box>
                            ) : (
                                <Text variant="body">
                                    <FormattedMessage
                                        id="annual.payment.delivery_by_courier_option_description"
                                        defaultMessage="{estimate_delivery_date} u Ciebie"
                                        values={{
                                            estimate_delivery_date:
                                                estimatedDeliveryDate,
                                        }}
                                    />
                                </Text>
                            ),
                        },
                    ]}
                    direction={isTablet ? 'column' : 'row'}
                    value={deliveryMethod}
                    setValue={handleSetDeliveryMethod}
                    isDisabled={isPostShippingMethodProcessing}
                    testIdPrefix="payment-delivery"
                    isFullWidth
                />
            </Box>

            {deliveryMethod === 'shipping' && <DeliveryByPostForm />}
        </Section>
    )
}

export default CardDeliveryOptions
